import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ConfirmModal } from "@/components/Common/ConfirmModal/ConfirmModal";
import { AmericanExpressIcon } from "@/components/Icons/AmericanExpressIcon";
import { MasterCardIcon } from "@/components/Icons/MasterCardIcon";
import { VisaIcon } from "@/components/Icons/VisaIcon";
import ENV_VARIABLES from "@/env";
import { cn } from "@/lib/utils";
import { useUserStore } from "@/store/user";
import { CheckoutTrustSignals } from "../ClusterPaymentOptions/CheckoutTrustSignals";
import { CreditsPaymentBreakdown } from "./CreditsPaymentBreakdown";
import {
  getCreditsPaymentBreakdown,
  readCreditsBalances,
  type CreditsTopUpPaymentMethod
} from "../VerticalFlowComponents/creditsPaymentAmount";

export type { CreditsTopUpPaymentMethod };

type CreditsPaymentConfirmModalProps = {
  isOpen: boolean;
  amount: number;
  /** Required paid-credits floor when paying with credits; 0 if none. */
  minNonPromotionalCreditsRequired?: number;
  /** Shows PAYG runtime planning when `amount` is the hourly deployment cost. */
  isPayg?: boolean;
  isLoading?: boolean;
  /** Modal heading. Defaults to the deploy copy. */
  title?: string;
  /** Confirm button label. Defaults to "Pay & Deploy". */
  submitLabel?: string;
  topUpPaymentMethod?: CreditsTopUpPaymentMethod;
  onTopUpPaymentMethodChange?: (method: CreditsTopUpPaymentMethod) => void;
  onConfirm: (paymentAmount: number) => void;
  onClose: () => void;
};

const STRIPE_FEE_LABEL = "5% Fee";
const USDC_INFO_URL = "https://www.usdc.com/";

function PaymentOptionFeeBadge({ label = STRIPE_FEE_LABEL }: { label?: string }) {
  return (
    <div className="label-x-small w-fit rounded-[4px] border border-gray-dark-200 bg-gray-dark-600 px-2 py-1 text-gray-dark-2200">
      {label}
    </div>
  );
}

function CreditCardBrandsCompact() {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex flex-row items-center gap-1.5">
        <div className="flex h-[18px] w-[28px] items-center justify-center rounded border border-border bg-black p-0.5">
          <VisaIcon className="h-[8px] w-auto" />
        </div>
        <div className="flex h-[18px] w-[28px] items-center justify-center rounded border border-border bg-black p-0.5">
          <MasterCardIcon className="h-[10px] w-auto" />
        </div>
        <div className="flex h-[18px] w-[28px] items-center justify-center overflow-hidden rounded border border-border">
          <AmericanExpressIcon className="h-full w-full" />
        </div>
      </div>
      <PaymentOptionFeeBadge />
    </div>
  );
}

function TopUpPaymentMethodSelector({
  value,
  onChange
}: {
  value: CreditsTopUpPaymentMethod;
  onChange: (method: CreditsTopUpPaymentMethod) => void;
}) {
  const isFiatEnabled = Boolean(ENV_VARIABLES.IS_FIAT_ENABLED);
  const options: Array<{
    value: CreditsTopUpPaymentMethod;
    label: ReactNode;
  }> = [
    ...(isFiatEnabled ? [{ value: "stripe" as const, label: <CreditCardBrandsCompact /> }] : []),
    {
      value: "usdc",
      label: (
        <div className="flex flex-row items-center gap-2">
          <img
            src="/assets/images/figma/payment-usdc-logo.svg"
            alt="USD backed stablecoin"
            className="h-[20px] w-[20px] shrink-0 object-contain"
          />
          <a
            href={USDC_INFO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[14px] font-medium leading-5 text-foreground hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            USD backed stablecoin
          </a>
        </div>
      )
    }
  ];

  if (options.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <p className="m-0 text-sm font-medium text-foreground">
        Payment method for additional credits
      </p>
      <div className="grid w-full grid-cols-1 gap-0 divide-y divide-border overflow-hidden rounded-lg border border-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={cn(
                "flex min-h-[56px] w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors",
                selected ? "bg-muted/40" : "bg-background hover:bg-muted/20"
              )}
            >
              <div className="min-w-0 flex-1">{option.label}</div>
              <div className="relative inline-grid shrink-0 place-items-start">
                <div className="col-1 row-1 size-[20px] rounded-full border border-border bg-background" />
                {selected && (
                  <div className="absolute left-[5px] top-[5px] size-[10px] rounded-full bg-primary" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CreditsPaymentDescription({
  amount,
  creditBalance,
  purchasedBalance,
  courtesyBalance = 0,
  minNonPromotionalCreditsRequired = 0,
  isPayg = false,
  topUpPaymentMethod,
  onTopUpPaymentMethodChange
}: {
  amount: number;
  creditBalance: number;
  purchasedBalance: number;
  courtesyBalance?: number;
  minNonPromotionalCreditsRequired?: number;
  isPayg?: boolean;
  topUpPaymentMethod: CreditsTopUpPaymentMethod;
  onTopUpPaymentMethodChange?: (method: CreditsTopUpPaymentMethod) => void;
}) {
  const { additionalRequired } = getCreditsPaymentBreakdown({
    totalCost: amount,
    creditsBalance: creditBalance,
    purchasedCreditsBalance: purchasedBalance,
    minNonPromotionalCreditsRequired,
    courtesyCreditsBalance: courtesyBalance,
    topUpPaymentMethod
  });

  const showTopUpMethod = additionalRequired > 0 && Boolean(onTopUpPaymentMethodChange);

  return (
    <div className="flex w-full max-w-full flex-col gap-5 pt-4">
      <CreditsPaymentBreakdown
        amount={amount}
        creditBalance={creditBalance}
        purchasedBalance={purchasedBalance}
        courtesyBalance={courtesyBalance}
        minNonPromotionalCreditsRequired={minNonPromotionalCreditsRequired}
        topUpPaymentMethod={topUpPaymentMethod}
        isPayg={isPayg}
      />

      {showTopUpMethod && (
        <TopUpPaymentMethodSelector
          value={topUpPaymentMethod}
          onChange={onTopUpPaymentMethodChange!}
        />
      )}
      <CheckoutTrustSignals
        currency={additionalRequired > 0 ? topUpPaymentMethod : "credits"}
        topUpRequired={additionalRequired > 0}
        isPayg={isPayg}
      />
    </div>
  );
}

const defaultTopUpMethod = (): CreditsTopUpPaymentMethod =>
  ENV_VARIABLES.IS_FIAT_ENABLED ? "stripe" : "usdc";

export const CreditsPaymentConfirmModal = ({
  isOpen,
  amount,
  minNonPromotionalCreditsRequired = 0,
  isPayg = false,
  isLoading = false,
  title = "Confirm payment with IO Credits",
  submitLabel = "Pay & Deploy",
  topUpPaymentMethod: controlledTopUpMethod,
  onTopUpPaymentMethodChange,
  onConfirm,
  onClose
}: CreditsPaymentConfirmModalProps) => {
  const balances = useUserStore((state) => state.balances);
  const fetchBalances = useUserStore((state) => state.fetchBalances);
  const [internalTopUpMethod, setInternalTopUpMethod] =
    useState<CreditsTopUpPaymentMethod>(defaultTopUpMethod);

  const topUpPaymentMethod = controlledTopUpMethod ?? internalTopUpMethod;

  const handleTopUpMethodChange = (method: CreditsTopUpPaymentMethod) => {
    if (onTopUpPaymentMethodChange) {
      onTopUpPaymentMethodChange(method);
    } else {
      setInternalTopUpMethod(method);
    }
  };

  useEffect(() => {
    if (isOpen) {
      void fetchBalances(true);
      if (!controlledTopUpMethod) {
        setInternalTopUpMethod(defaultTopUpMethod());
      }
    }
  }, [fetchBalances, isOpen, controlledTopUpMethod]);

  const { creditBalance, purchasedBalance, courtesyBalance } = useMemo(() => {
    const { creditsBalance, purchasedCreditsBalance, courtesyCreditsBalance } =
      readCreditsBalances(balances);
    return {
      creditBalance: creditsBalance,
      purchasedBalance: purchasedCreditsBalance,
      courtesyBalance: courtesyCreditsBalance
    };
  }, [balances]);

  return (
    <ConfirmModal
      title={title}
      description={
        <CreditsPaymentDescription
          amount={amount}
          creditBalance={creditBalance}
          purchasedBalance={purchasedBalance}
          courtesyBalance={courtesyBalance}
          minNonPromotionalCreditsRequired={minNonPromotionalCreditsRequired}
          isPayg={isPayg}
          topUpPaymentMethod={topUpPaymentMethod}
          onTopUpPaymentMethodChange={handleTopUpMethodChange}
        />
      }
      submitLabel={submitLabel}
      submitButtonVariant="standout"
      isLoading={isLoading}
      isOpen={isOpen}
      onSubmit={() => onConfirm(amount)}
      onClose={onClose}
      className="sm:max-w-lg"
    />
  );
};
